import { FormattedMessage } from 'react-intl';
import { useState } from 'react';
import { ClearFilterButton } from 'components/filter/input/ClearFilterButton';
import { useAppStore } from 'hooks/state/useAppStore';
import { FilterData } from 'hooks/state/FilterSlice';
import { Module, MODULE_FILTER_CONFIG, FilterItem } from './moduleConfig';
import { FilterDialogManager } from './FilterDialogManager';
import { FilterFormController } from './FilterFormController';
import { Button } from 'components/Button';

export interface AdvancedFilterButtonProps {
  module: Module;
  disabled?: boolean;
}

// Filter types that map to an array-valued filter field in the store and should
// count towards the "active filters" badge. Types not listed here (e.g. dateRange,
// dateRangeComparison, creationDateRange, benchmark) are excluded from the count.
const COUNTABLE_FILTER_TYPES = new Set<FilterItem['type']>([
  'brands',
  'marketplaces',
  'countries',
  'categories',
  'seasons',
  'genders',
  'ageGroups',
  'fulfillmentMethods',
  'products',
  'colors',
  'sizes',
]);

function countActiveFilters(module: Module, state: FilterData): number {
  const sections = MODULE_FILTER_CONFIG[module].available ?? [];

  return sections
    .flat()
    .filter(({ type }) => COUNTABLE_FILTER_TYPES.has(type))
    .reduce((count, { type }) => {
      const value = state[type as keyof FilterData] as string[] | undefined;
      return count + (value && value.length > 0 ? 1 : 0);
    }, 0);
}

export function FilterDialogButton({ module, disabled = false }: AdvancedFilterButtonProps) {
  const [showAdvancedFilter, setShowAdvancedFilter] = useState(false);

  const activeFilterCount = useAppStore(state => countActiveFilters(module, state));

  return (
    <FilterFormController onClose={() => setShowAdvancedFilter(false)}>
      <div className="flex gap-2 items-center">
        <Button
          priority="secondary"
          iconSize="lg"
          onClick={() => {
            setShowAdvancedFilter(true);
          }}
          className="relative"
          id="advanced-filter-dialog-trigger-button"
          disabled={disabled}
          iconLeft="Equalizer"
        >
          <FormattedMessage id="advanced-filter-button-title" defaultMessage="All" />
          {activeFilterCount > 0 && (
            <span className="absolute top-0 right-0 h-[18px] min-w-[18px] px-0.5 flex items-center justify-center z-50 bg-black text-white text-[10px] font-bold leading-none rounded-full transform translate-x-1/2 -translate-y-1/2">
              {activeFilterCount}
            </span>
          )}
        </Button>
        <ClearFilterButton />
        <FilterDialogManager open={showAdvancedFilter} onClose={() => setShowAdvancedFilter(false)} module={module} />
      </div>
    </FilterFormController>
  );
}
